import { ArrowRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { Task } from '../../types/tasks';
import { getAgentBySlug, getAgentColor } from '../../utils/agents';

const PRIORITY_STYLES: Record<string, string> = {
  urgent: 'bg-error/15 text-error',
  high: 'bg-warning/15 text-warning',
  medium: 'bg-info/15 text-info',
  low: 'bg-bg-tertiary text-text-muted',
};

const CADENCE_STYLES: Record<string, string> = {
  daily: 'bg-accent-purple/15 text-accent-purple-light',
  weekly: 'bg-info/15 text-info',
  biweekly: 'bg-avatar-cyan/15 text-avatar-cyan',
  monthly: 'bg-success/15 text-success',
  quarterly: 'bg-warning/15 text-warning',
  trigger: 'bg-error/15 text-error',
};

const NEXT_STATUS: Record<string, string> = {
  backlog: 'in_progress',
  in_progress: 'completed',
};

interface TaskCardProps {
  task: Task;
  onStatusChange: (id: number, status: string) => void;
  onClick: () => void;
}

export default function TaskCard({ task, onStatusChange, onClick }: TaskCardProps) {
  const agent = task.assigned_agent ? getAgentBySlug(task.assigned_agent) : null;
  const nextStatus = NEXT_STATUS[task.status];
  const priorityStyle = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.medium;

  function handleAdvance(e: React.MouseEvent) {
    e.stopPropagation();
    if (nextStatus) {
      onStatusChange(task.id, nextStatus);
    }
  }

  return (
    <div
      className="bg-bg-secondary border border-border-subtle rounded-lg p-3 hover:border-border-hover cursor-pointer transition-colors"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="font-medium text-text-primary text-sm leading-snug">{task.title}</h4>
        <div className="flex shrink-0 items-center gap-1.5">
          {task.cadence ? (
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${CADENCE_STYLES[task.cadence] || 'bg-bg-tertiary text-text-muted'}`}>
              {task.cadence}
            </span>
          ) : null}
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${priorityStyle}`}>
            {task.priority}
          </span>
        </div>
      </div>

      {task.department ? (
        <p className="text-xs text-text-muted mb-2">{task.department}</p>
      ) : null}

      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-2">
          {agent ? (
            <div className="flex items-center gap-1.5">
              <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-medium text-white ${getAgentColor(agent.department)}`}>
                {agent.name.charAt(0)}
              </div>
              <span className="text-xs text-text-secondary">{agent.name}</span>
            </div>
          ) : (
            <span className="text-xs text-text-muted">Unassigned</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-text-muted">
            {formatDistanceToNow(new Date(task.created_at), { addSuffix: true })}
          </span>
          {nextStatus ? (
            <button
              onClick={handleAdvance}
              className="p-1 rounded hover:bg-bg-tertiary text-text-muted hover:text-accent-purple-light transition-colors"
              title={`Move to ${nextStatus.replace('_', ' ')}`}
            >
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
